import { WEB_ROUTE_SEGMENTS } from './constants'

const SUPPORTED_LOCALES = ['cs', 'en', 'de', 'pl'] as const
export const CHECKOUT_RETURN_TO_QUERY_PARAM_KEY = 'return_to'
export const CHECKOUT_FLOW_QUERY_PARAM_KEY = 'checkout_flow'
export const CHECKOUT_RESERVATION_TOKEN_QUERY_PARAM_KEY = 'reservation_token'

export type CheckoutFlow = 'reservation' | 'prolongation' | 'change_unit'

export function isAdditionalPaymentCheckoutFlow(
    checkoutFlow: CheckoutFlow | null | undefined,
): boolean {
    return checkoutFlow === 'prolongation' || checkoutFlow === 'change_unit'
}

type CheckoutLocale = typeof SUPPORTED_LOCALES[number]

type CheckoutPathOptions = {
    isPwa: boolean,
    locale: string | null | undefined,
    searchParams?: string | URLSearchParams | null,
    returnTo?: string | null,
    checkoutFlow?: CheckoutFlow,
}

function getBaseCheckoutPath({
    isPwa,
    locale,
}: {
    isPwa: boolean,
    locale: string | null | undefined,
}): string {
    if (isPwa) {
        return '/pwa/checkout'
    }

    const normalizedLocale = normalizeCheckoutLocale(locale)

    return `/${normalizedLocale}/${WEB_ROUTE_SEGMENTS.checkout[normalizedLocale]}`
}

function toURLSearchParams(searchParams?: string | URLSearchParams | null): URLSearchParams {
    if (!searchParams) {
        return new URLSearchParams()
    }

    if (searchParams instanceof URLSearchParams) {
        return new URLSearchParams(searchParams)
    }

    return new URLSearchParams(searchParams.startsWith('?') ? searchParams.slice(1) : searchParams)
}

function buildSearch({
    searchParams,
    returnTo,
    paymentId,
    checkoutFlow,
}: {
    searchParams?: string | URLSearchParams | null,
    returnTo?: string | null,
    paymentId?: number | string,
    checkoutFlow?: CheckoutFlow,
}): string {
    const params = toURLSearchParams(searchParams)

    if (returnTo !== undefined) {
        if (returnTo) {
            params.set(CHECKOUT_RETURN_TO_QUERY_PARAM_KEY, returnTo)
        } else {
            params.delete(CHECKOUT_RETURN_TO_QUERY_PARAM_KEY)
        }
    }

    if (paymentId !== undefined) {
        params.set('payment_id', paymentId.toString())
    }

    if (checkoutFlow !== undefined) {
        if (isAdditionalPaymentCheckoutFlow(checkoutFlow)) {
            params.set(CHECKOUT_FLOW_QUERY_PARAM_KEY, checkoutFlow)
        } else {
            params.delete(CHECKOUT_FLOW_QUERY_PARAM_KEY)
        }
    }

    const queryString = params.toString()

    return queryString ? `?${queryString}` : ''
}

export function normalizeCheckoutLocale(locale: string | null | undefined): CheckoutLocale {
    const normalizedLocale = locale?.toLowerCase().split(/[-_]/)[0]

    if (SUPPORTED_LOCALES.includes(normalizedLocale as CheckoutLocale)) {
        return normalizedLocale as CheckoutLocale
    }

    return 'cs'
}

export function normalizeCheckoutFlow(flow: string | null | undefined): CheckoutFlow {
    if (flow === 'prolongation' || flow === 'change_unit') {
        return flow
    }

    return 'reservation'
}

export function getCheckoutPath({
    isPwa,
    locale,
    searchParams,
    returnTo,
    checkoutFlow,
}: CheckoutPathOptions): string {
    return `${getBaseCheckoutPath({ isPwa, locale })}${buildSearch({ searchParams, returnTo, checkoutFlow })}`
}

export function getCheckoutFallbackPath(locale: string | null | undefined): string {
    const normalizedLocale = normalizeCheckoutLocale(locale)

    return `/${normalizedLocale}/${WEB_ROUTE_SEGMENTS.garageMap[normalizedLocale]}`
}

export function getValidatedCheckoutReturnTo(searchParams: string | URLSearchParams): string | null {
    const params = toURLSearchParams(searchParams)
    const returnTo = params.get(CHECKOUT_RETURN_TO_QUERY_PARAM_KEY)

    if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
        return null
    }

    if (typeof window === 'undefined') {
        return returnTo
    }

    try {
        const url = new URL(returnTo, window.location.origin)

        if (url.origin !== window.location.origin) {
            return null
        }

        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        return null
    }
}

export function getCheckoutSuccessPath({
    isPwa,
    locale,
    paymentId,
    searchParams,
    returnTo,
    checkoutFlow,
}: CheckoutPathOptions & {
    paymentId: number | string,
}): string {
    return `${getBaseCheckoutPath({ isPwa, locale })}/success${buildSearch({ searchParams, returnTo, paymentId, checkoutFlow })}`
}

export function getCheckoutPaymentPath({
    isPwa,
    locale,
    searchParams,
    returnTo,
    checkoutFlow,
}: CheckoutPathOptions): string {
    return `${getBaseCheckoutPath({ isPwa, locale })}/payment${buildSearch({ searchParams, returnTo, checkoutFlow })}`
}

export function getCheckoutCancelPath({
    isPwa,
    locale,
    searchParams,
    returnTo,
    checkoutFlow,
}: CheckoutPathOptions): string {
    if (isAdditionalPaymentCheckoutFlow(checkoutFlow) && returnTo) {
        return returnTo
    }

    return getCheckoutPaymentPath({
        isPwa,
        locale,
        searchParams,
        returnTo,
        checkoutFlow,
    })
}

export function getCheckoutPaymentGuardRedirectPath({
    isPwa,
    locale,
    searchParams,
    checkoutUiMode,
}: CheckoutPathOptions & {
    checkoutUiMode: 'embedded' | 'hosted' | 'native_ios',
}): string | null {
    if (checkoutUiMode === 'embedded') {
        return null
    }

    return getCheckoutPath({
        isPwa,
        locale,
        searchParams,
    })
}

export function getCheckoutFlow(searchParams?: string | URLSearchParams | null): CheckoutFlow {
    const params = toURLSearchParams(searchParams)

    return normalizeCheckoutFlow(params.get(CHECKOUT_FLOW_QUERY_PARAM_KEY))
}

export function getCheckoutReservationToken(
    searchParams?: string | URLSearchParams | null,
): string | null {
    return toURLSearchParams(searchParams).get(CHECKOUT_RESERVATION_TOKEN_QUERY_PARAM_KEY)
}

export function hasCheckoutHistoryEntry(historyState: unknown): boolean {
    if (!historyState || typeof historyState !== 'object') {
        return false
    }

    const { idx } = historyState as { idx?: unknown }

    return typeof idx === 'number' && idx > 0
}
